import type { VisibilityState } from '@tanstack/react-table';
import { createContext, PropsWithChildren, useContext, useState } from 'react';

export interface LocalSettings {
  campaign: {
    view: 'calendar' | 'list';
    /** Persisted column visibility for the campaigns list table (analytics columns). */
    listTableColumnVisibility: VisibilityState;
  };
  metricsPageTourCompleted: boolean;
  analyticsAnnouncementModalDismissed: boolean;
  foundersOnlyPromoDismissed: boolean;
  integrationUpdateModalDismissedTenantSlugs: Record<string, true>;
}

const defaultValues: LocalSettings = {
  campaign: {
    view: 'list',
    listTableColumnVisibility: {},
  },
  metricsPageTourCompleted: false,
  analyticsAnnouncementModalDismissed: false,
  foundersOnlyPromoDismissed: false,
  integrationUpdateModalDismissedTenantSlugs: {},
};

interface LocalSettingsContextProps {
  localSettings: LocalSettings;
  setLocalSettings: (settings: LocalSettings | null) => void;
}

const LocalSettingsContext = createContext<LocalSettingsContextProps>({
  localSettings: defaultValues,
  setLocalSettings: () => {},
});

export function useLocalSettings() {
  return useContext(LocalSettingsContext);
}

function mergeWithDefaults(
  stored: Partial<LocalSettings> | LocalSettings | null
): LocalSettings {
  return {
    ...defaultValues,
    ...(stored ?? {}),
    campaign: {
      ...defaultValues.campaign,
      ...stored?.campaign,
    },
    integrationUpdateModalDismissedTenantSlugs: {
      ...defaultValues.integrationUpdateModalDismissedTenantSlugs,
      ...stored?.integrationUpdateModalDismissedTenantSlugs,
    },
  };
}

export function LocalSettingsProvider({ children }: PropsWithChildren) {
  const [localSettings, setLocalSettings] = useState<LocalSettings>(() =>
    mergeWithDefaults(getStoredLocalSettings())
  );

  const saveLocalSettings = (settings: LocalSettings | null) => {
    const newSettings = mergeWithDefaults(settings);
    setLocalSettings(newSettings);
    storeLocalSettings(newSettings);
  };

  const value: LocalSettingsContextProps = {
    localSettings,
    setLocalSettings: saveLocalSettings,
  };

  return (
    <LocalSettingsContext.Provider value={value}>
      {children}
    </LocalSettingsContext.Provider>
  );
}

const LOCAL_STORAGE_KEY = 'local-settings';

function storeLocalSettings(payload: LocalSettings | null) {
  if (payload === null) {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } else {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(payload));
  }
}

function getStoredLocalSettings(): Partial<LocalSettings> | null {
  const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
  if (stored === null) {
    return null;
  }

  return JSON.parse(stored) as Partial<LocalSettings>;
}
